import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  User,
  Target,
  FileText,
  Calendar,
  CheckCircle,
  Clock,
  TrendingUp,
  ChevronRight,
  Eye,
  Download,
  MessageSquare,
  FileCheck,
  FileX,
  File,
  Loader2,
  ArrowLeft,
  Award,
  BookOpen,
  Upload,
  ThumbsUp,
  ThumbsDown,
  Send
} from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    ingediend: 'bg-blue-100 text-blue-700',
    in_beoordeling: 'bg-amber-100 text-amber-700',
    goedgekeurd: 'bg-green-100 text-green-700',
    afgekeurd: 'bg-red-100 text-red-700',
    nieuw: 'bg-slate-100 text-slate-600',
    bezig: 'bg-blue-100 text-blue-700',
    afgerond: 'bg-green-100 text-green-700'
  };
  
  const labels = {
    ingediend: 'Ingediend',
    in_beoordeling: 'In beoordeling',
    goedgekeurd: 'Goedgekeurd',
    afgekeurd: 'Afgekeurd',
    nieuw: 'Nieuw',
    bezig: 'Bezig',
    afgerond: 'Afgerond'
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
      {labels[status] || status}
    </span>
  );
};

export default function WerkbegeleiderDashboard({ user, token }) {
  const [stagiaires, setStagiaires] = useState([]);
  const [selectedStagiaire, setSelectedStagiaire] = useState(null);
  const [stagiairDetail, setStagiaireDetail] = useState(null);
  const [opdrachten, setOpdrachten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overzicht'); // overzicht, detail
  
  // Beoordeling modal
  const [showBeoordeling, setShowBeoordeling] = useState(null);
  const [beoordelingData, setBeoordelingData] = useState({ status: '', feedback: '' });
  const [beoordelingLoading, setBeoordelingLoading] = useState(false);
  
  // Leerdoel beoordeling modal
  const [showLeerdoelBeoordeling, setShowLeerdoelBeoordeling] = useState(null);
  const [leerdoelBeoordelingData, setLeerdoelBeoordelingData] = useState({ status: '', feedback: '' });
  const [leerdoelBeoordelingLoading, setLeerdoelBeoordelingLoading] = useState(false);
  const [wbBewijsFile, setWbBewijsFile] = useState(null);

  useEffect(() => {
    fetchStagiaires();
    fetchAlleOpdrachten();
  }, []);

  const fetchStagiaires = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/werkbegeleider/stagiaires`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStagiaires(response.data);
    } catch (error) {
      console.error('Error fetching stagiaires:', error);
      if (error.response?.status === 403) {
        toast.error('Je hebt geen werkbegeleider rechten');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAlleOpdrachten = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/werkbegeleider/opdrachten`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpdrachten(response.data);
    } catch (error) {
      console.error('Error fetching opdrachten:', error);
    }
  };

  const fetchStagiaireDetail = async (stagiaire_id) => {
    try {
      const response = await axios.get(`${API_URL}/api/werkbegeleider/stagiaire/${stagiaire_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStagiaireDetail(response.data);
      setActiveView('detail');
    } catch (error) {
      toast.error('Kon stagiaire details niet laden');
    }
  };

  const handleBeoordeling = async () => {
    if (!beoordelingData.status) {
      toast.error('Selecteer een status');
      return;
    }
    if (!beoordelingData.feedback.trim()) {
      toast.error('Vul feedback in');
      return;
    }

    setBeoordelingLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/werkbegeleider/opdrachten/${showBeoordeling}/beoordelen`,
        beoordelingData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Opdracht beoordeeld!');
      setShowBeoordeling(null);
      setBeoordelingData({ status: '', feedback: '' });
      fetchAlleOpdrachten();
      if (selectedStagiaire) {
        fetchStagiaireDetail(selectedStagiaire);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij beoordelen');
    } finally {
      setBeoordelingLoading(false);
    }
  };

  const downloadOpdracht = async (opdracht) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/stagiaire/opdrachten/${opdracht.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', opdracht.bestand_naam);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Fout bij downloaden bestand');
    }
  };

  const downloadLeerdoelBewijs = async (leerdoel) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/stagiaire/leerdoelen/${leerdoel.id}/bewijs`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', leerdoel.bewijs_naam);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Fout bij downloaden bewijs');
    }
  };

  // Leerdoel beoordeling functies
  const handleLeerdoelBeoordeling = async () => {
    if (!leerdoelBeoordelingData.status) {
      toast.error('Selecteer een status');
      return;
    }
    
    setLeerdoelBeoordelingLoading(true);
    try {
      // First, submit the review
      await axios.put(
        `${API_URL}/api/werkbegeleider/leerdoel/${showLeerdoelBeoordeling.id}/beoordelen`,
        {
          status: leerdoelBeoordelingData.status,
          feedback: leerdoelBeoordelingData.feedback
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // If there's a file, upload it
      if (wbBewijsFile) {
        const formData = new FormData();
        formData.append('bestand', wbBewijsFile);
        await axios.post(
          `${API_URL}/api/werkbegeleider/leerdoel/${showLeerdoelBeoordeling.id}/bewijs`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      
      toast.success('Leerdoel beoordeeld!');
      setShowLeerdoelBeoordeling(null);
      setLeerdoelBeoordelingData({ status: '', feedback: '' });
      setWbBewijsFile(null);
      
      // Refresh the stagiaire detail
      if (selectedStagiaire) {
        fetchStagiaireDetail(selectedStagiaire);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij beoordelen leerdoel');
    } finally {
      setLeerdoelBeoordelingLoading(false);
    }
  };

  const pendingOpdrachten = opdrachten.filter(o => o.status === 'ingediend');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 rounded-2xl p-6 text-white"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
            <Users className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Werkbegeleider Dashboard</h1>
            <p className="text-blue-100">Welkom, {user?.name}</p>
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
            <div className="text-3xl font-bold">{stagiaires.length}</div>
            <div className="text-sm text-blue-100">Stagiaires</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
            <div className="text-3xl font-bold">{pendingOpdrachten.length}</div>
            <div className="text-sm text-blue-100">Te Beoordelen</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
            <div className="text-3xl font-bold">{opdrachten.length}</div>
            <div className="text-sm text-blue-100">Totaal Opdrachten</div>
          </div>
        </div>
      </motion.div>

      {activeView === 'overzicht' ? (
        <>
          {/* Pending Opdrachten Alert */}
          {pendingOpdrachten.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-amber-50 border border-amber-200 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800">
                    {pendingOpdrachten.length} opdracht(en) wachten op beoordeling
                  </h3>
                  <p className="text-sm text-amber-600">Bekijk de opdrachten hieronder</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Stagiaires Grid */}
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Mijn Stagiaires
            </h2>
            
            {stagiaires.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Nog geen stagiaires aan je gekoppeld</p>
                <p className="text-sm text-slate-400 mt-1">
                  Vraag een admin om stagiaires aan je te koppelen
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stagiaires.map((stagiaire) => (
                  <motion.div
                    key={stagiaire.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.02 }}
                    className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all cursor-pointer"
                    onClick={() => {
                      setSelectedStagiaire(stagiaire.id);
                      fetchStagiaireDetail(stagiaire.id);
                    }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {stagiaire.name?.charAt(0) || 'S'}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800">{stagiaire.name}</h3>
                        <p className="text-sm text-slate-500">{stagiaire.email}</p>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">Voortgang</span>
                        <span className="font-medium text-blue-600">{stagiaire.voortgang || 0}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${stagiaire.voortgang || 0}%` }}
                          className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                        />
                      </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100">
                      {stagiaire.pending_opdrachten > 0 && (
                        <div className="flex items-center gap-1 text-amber-600">
                          <FileText className="w-4 h-4" />
                          <span className="text-sm font-medium">{stagiaire.pending_opdrachten} te beoordelen</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-slate-400 ml-auto">
                        <span className="text-sm">Bekijk</span>
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Opdrachten */}
          {opdrachten.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Recente Opdrachten
              </h2>
              <div className="space-y-3">
                {opdrachten.slice(0, 5).map((opdracht) => (
                  <motion.div
                    key={opdracht.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`p-2 rounded-lg ${
                          opdracht.status === 'goedgekeurd' ? 'bg-green-100' :
                          opdracht.status === 'afgekeurd' ? 'bg-red-100' :
                          opdracht.status === 'ingediend' ? 'bg-blue-100' :
                          'bg-slate-100'
                        }`}>
                          {opdracht.status === 'goedgekeurd' ? (
                            <FileCheck className="w-5 h-5 text-green-600" />
                          ) : opdracht.status === 'afgekeurd' ? (
                            <FileX className="w-5 h-5 text-red-600" />
                          ) : (
                            <File className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-medium text-slate-800 truncate">{opdracht.titel}</h4>
                          <p className="text-sm text-slate-500">
                            {opdracht.stagiaire_naam} • {new Date(opdracht.created_at).toLocaleDateString('nl-NL')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={opdracht.status} />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadOpdracht(opdracht)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {opdracht.status === 'ingediend' && (
                          <Button
                            size="sm"
                            onClick={() => setShowBeoordeling(opdracht.id)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Beoordelen
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        /* Detail View */
        stagiairDetail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Back Button & Header */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => {
                  setActiveView('overzicht');
                  setStagiaireDetail(null);
                  setSelectedStagiaire(null);
                }}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Terug
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                  {stagiairDetail.stagiaire?.name?.charAt(0) || 'S'}
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">{stagiairDetail.stagiaire?.name}</h2>
                  <p className="text-sm text-slate-500">{stagiairDetail.stagiaire?.email}</p>
                </div>
              </div>
            </div>

            {/* Voortgang Overview */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  Stage Voortgang
                </h3>
                <span className="text-2xl font-bold text-purple-600">
                  {stagiairDetail.voortgang?.percentage || 0}%
                </span>
              </div>
              <div className="h-3 bg-white rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${stagiairDetail.voortgang?.percentage || 0}%` }}
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                />
              </div>
              <div className="flex justify-between text-sm mt-2 text-slate-600">
                <span>{stagiairDetail.voortgang?.afgerond || 0} afgerond</span>
                <span>{stagiairDetail.voortgang?.totaal || 0} leerdoelen totaal</span>
              </div>
            </div>

            {/* Tabs Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Leerdoelen */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  Leerdoelen ({stagiairDetail.leerdoelen?.length || 0})
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {stagiairDetail.leerdoelen?.map((doel) => (
                    <div key={doel.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${
                          doel.status === 'goedgekeurd' ? 'bg-green-500' :
                          doel.status === 'afgekeurd' ? 'bg-red-500' :
                          doel.status === 'afgerond' ? 'bg-green-500' :
                          doel.status === 'bezig' ? 'bg-blue-500' :
                          'bg-slate-300'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm text-slate-700">{doel.doel}</span>
                            <StatusBadge status={doel.status} />
                          </div>
                          {doel.reflectie && (
                            <p className="text-xs text-slate-500 mt-1 italic">💭 {doel.reflectie}</p>
                          )}
                          
                          {/* Bewijs van stagiaire */}
                          {doel.bewijs_naam && (
                            <div className="flex items-center gap-2 mt-2 bg-green-100 rounded px-2 py-1">
                              <FileCheck className="w-3 h-3 text-green-600" />
                              <span className="text-xs text-green-700 flex-1 truncate">{doel.bewijs_naam}</span>
                              <button
                                onClick={() => downloadLeerdoelBewijs(doel)}
                                className="p-1 hover:bg-green-200 rounded text-green-600"
                                title="Download bewijs"
                              >
                                <Download className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          
                          {/* Werkbegeleider feedback */}
                          {doel.wb_feedback && (
                            <div className={`mt-2 p-2 rounded text-xs ${
                              doel.status === 'goedgekeurd' ? 'bg-green-50 border border-green-200' :
                              doel.status === 'afgekeurd' ? 'bg-red-50 border border-red-200' :
                              'bg-blue-50 border border-blue-200'
                            }`}>
                              <p className="font-medium text-slate-600">Jouw feedback:</p>
                              <p className="text-slate-700">{doel.wb_feedback}</p>
                              {doel.wb_bewijs_naam && (
                                <p className="text-slate-500 mt-1 flex items-center gap-1">
                                  <File className="w-3 h-3" /> {doel.wb_bewijs_naam}
                                </p>
                              )}
                            </div>
                          )}
                          
                          {/* Beoordeling knop - alleen tonen als er bewijs is en nog niet beoordeeld */}
                          {doel.bewijs_naam && !doel.wb_feedback && (
                            <button
                              onClick={() => {
                                setShowLeerdoelBeoordeling(doel);
                                setLeerdoelBeoordelingData({ status: '', feedback: '' });
                                setWbBewijsFile(null);
                              }}
                              className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors"
                              data-testid={`beoordeel-leerdoel-${doel.id}`}
                            >
                              <Send className="w-3 h-3" />
                              Beoordelen
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {stagiairDetail.leerdoelen?.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">Nog geen leerdoelen</p>
                  )}
                </div>
              </div>

              {/* Gesprekken */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-600" />
                  Gesprekken ({stagiairDetail.gesprekken?.length || 0})
                </h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {stagiairDetail.gesprekken?.map((gesprek) => (
                    <div key={gesprek.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">{gesprek.type}</p>
                        <p className="text-xs text-slate-500">{gesprek.datum} om {gesprek.tijd}</p>
                      </div>
                      <StatusBadge status={gesprek.status} />
                    </div>
                  ))}
                  {stagiairDetail.gesprekken?.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">Nog geen gesprekken gepland</p>
                  )}
                </div>
              </div>
            </div>

            {/* Opdrachten */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Opdrachten ({stagiairDetail.opdrachten?.length || 0})
              </h3>
              <div className="space-y-3">
                {stagiairDetail.opdrachten?.map((opdracht) => (
                  <div 
                    key={opdracht.id} 
                    className="flex items-center justify-between gap-4 p-4 bg-slate-50 rounded-xl"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${
                        opdracht.status === 'goedgekeurd' ? 'bg-green-100' :
                        opdracht.status === 'afgekeurd' ? 'bg-red-100' :
                        'bg-blue-100'
                      }`}>
                        {opdracht.status === 'goedgekeurd' ? (
                          <FileCheck className="w-5 h-5 text-green-600" />
                        ) : opdracht.status === 'afgekeurd' ? (
                          <FileX className="w-5 h-5 text-red-600" />
                        ) : (
                          <File className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-slate-800 truncate">{opdracht.titel}</h4>
                        <p className="text-sm text-slate-500">
                          {opdracht.bestand_naam} • {new Date(opdracht.created_at).toLocaleDateString('nl-NL')}
                        </p>
                        {opdracht.feedback && (
                          <p className="text-sm text-slate-600 mt-1 italic">"{opdracht.feedback}"</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={opdracht.status} />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadOpdracht(opdracht)}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {opdracht.status === 'ingediend' && (
                        <Button
                          size="sm"
                          onClick={() => setShowBeoordeling(opdracht.id)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Beoordelen
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {stagiairDetail.opdrachten?.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">Nog geen opdrachten ingediend</p>
                )}
              </div>
            </div>
          </motion.div>
        )
      )}

      {/* Beoordeling Modal */}
      <Dialog open={!!showBeoordeling} onOpenChange={() => {
        setShowBeoordeling(null);
        setBeoordelingData({ status: '', feedback: '' });
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Opdracht Beoordelen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Beoordeling *</label>
              <div className="flex gap-3 mt-2">
                <button
                  onClick={() => setBeoordelingData({ ...beoordelingData, status: 'goedgekeurd' })}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    beoordelingData.status === 'goedgekeurd'
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-green-300'
                  }`}
                >
                  <FileCheck className={`w-8 h-8 mx-auto mb-2 ${
                    beoordelingData.status === 'goedgekeurd' ? 'text-green-600' : 'text-slate-400'
                  }`} />
                  <span className={`text-sm font-medium ${
                    beoordelingData.status === 'goedgekeurd' ? 'text-green-700' : 'text-slate-600'
                  }`}>Goedgekeurd</span>
                </button>
                <button
                  onClick={() => setBeoordelingData({ ...beoordelingData, status: 'afgekeurd' })}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    beoordelingData.status === 'afgekeurd'
                      ? 'border-red-500 bg-red-50'
                      : 'border-slate-200 hover:border-red-300'
                  }`}
                >
                  <FileX className={`w-8 h-8 mx-auto mb-2 ${
                    beoordelingData.status === 'afgekeurd' ? 'text-red-600' : 'text-slate-400'
                  }`} />
                  <span className={`text-sm font-medium ${
                    beoordelingData.status === 'afgekeurd' ? 'text-red-700' : 'text-slate-600'
                  }`}>Feedback Nodig</span>
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Feedback *</label>
              <textarea
                value={beoordelingData.feedback}
                onChange={(e) => setBeoordelingData({ ...beoordelingData, feedback: e.target.value })}
                placeholder="Geef feedback aan de stagiaire..."
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[120px]"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => {
                setShowBeoordeling(null);
                setBeoordelingData({ status: '', feedback: '' });
              }}>
                Annuleren
              </Button>
              <Button 
                onClick={handleBeoordeling}
                disabled={beoordelingLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {beoordelingLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Verzenden...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Beoordeling Opslaan
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leerdoel Beoordeling Modal */}
      <Dialog open={!!showLeerdoelBeoordeling} onOpenChange={() => {
        setShowLeerdoelBeoordeling(null);
        setLeerdoelBeoordelingData({ status: '', feedback: '' });
        setWbBewijsFile(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Leerdoel Beoordelen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Leerdoel info */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700">{showLeerdoelBeoordeling?.doel}</p>
              {showLeerdoelBeoordeling?.reflectie && (
                <p className="text-xs text-slate-500 mt-1 italic">💭 {showLeerdoelBeoordeling?.reflectie}</p>
              )}
            </div>
            
            {/* Status selectie */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Beoordeling *</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setLeerdoelBeoordelingData({ ...leerdoelBeoordelingData, status: 'goedgekeurd' })}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                    leerdoelBeoordelingData.status === 'goedgekeurd'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-slate-200 hover:border-green-300'
                  }`}
                  data-testid="leerdoel-goedkeuren-btn"
                >
                  <ThumbsUp className="w-5 h-5" />
                  <span className="font-medium">Goedkeuren</span>
                </button>
                <button
                  onClick={() => setLeerdoelBeoordelingData({ ...leerdoelBeoordelingData, status: 'afgekeurd' })}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                    leerdoelBeoordelingData.status === 'afgekeurd'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-slate-200 hover:border-red-300'
                  }`}
                  data-testid="leerdoel-afkeuren-btn"
                >
                  <ThumbsDown className="w-5 h-5" />
                  <span className="font-medium">Afkeuren</span>
                </button>
              </div>
            </div>
            
            {/* Feedback */}
            <div>
              <label className="text-sm font-medium text-slate-700">Feedback aan stagiaire</label>
              <textarea
                value={leerdoelBeoordelingData.feedback}
                onChange={(e) => setLeerdoelBeoordelingData({ ...leerdoelBeoordelingData, feedback: e.target.value })}
                placeholder="Geef feedback over het leerdoel..."
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[100px]"
              />
            </div>
            
            {/* Bewijs upload */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">
                Feedback document (optioneel)
              </label>
              <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                <Upload className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">
                  {wbBewijsFile ? wbBewijsFile.name : 'Upload foto, video of document'}
                </span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.mov,image/*,video/*"
                  onChange={(e) => setWbBewijsFile(e.target.files[0])}
                  className="hidden"
                />
              </label>
            </div>
            
            {/* Buttons */}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => {
                setShowLeerdoelBeoordeling(null);
                setLeerdoelBeoordelingData({ status: '', feedback: '' });
                setWbBewijsFile(null);
              }}>
                Annuleren
              </Button>
              <Button 
                onClick={handleLeerdoelBeoordeling}
                disabled={leerdoelBeoordelingLoading || !leerdoelBeoordelingData.status}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="leerdoel-beoordeling-submit"
              >
                {leerdoelBeoordelingLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Verzenden...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-1" />
                    Beoordeling Opslaan
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
